import React, { useContext, useEffect, useState } from "react";
import { Col, Form, Input, Row, Select, Tag, Typography } from "antd";
import inputStypes from "../../../../styles/Custom/Input.module.css";
import { getPractitionersListApi } from "../../../../services/doctor.services";
import { UserContext } from "../../../../context/UserContext";
import { getCookie } from "../../../../helpers/localStorage";
import { FacilityID } from "../../../../constants/defaultKeys";
import "../FormsIp.css";

const FIELD_COL = { xs: 24, sm: 12, md: 12, lg: 8 };

const ConsultantForm = ({ form }) => {
  const { userDetails } = useContext(UserContext);
  const [consultants, setConsultants] = useState([]);
  const [physiotherapists, setPhysiotherapists] = useState([]);
  const [nutritionists, setNutritionists] = useState([]);

  const facilityId = getCookie(FacilityID) || userDetails?.currFacility;

  /*
   * The head of department is filled in for you.
   *
   * The practitioners list marks one of each role `is_hod`, and that is who
   * signs the form unless somebody says otherwise - so the field opens on
   * them rather than empty. Only ever filled where the field has nothing in
   * it: a form being edited already carries whoever was chosen when it was
   * written, and that must not be overwritten on load.
   */
  const prefillHeadOfDepartment = (list, prefix) => {
    const hod = (list || []).find((person) => person?.is_hod);
    if (!hod) return;

    const chosen = form.getFieldValue(["consultants", prefix]);
    if (chosen !== undefined && chosen !== null && chosen !== "") return;

    form.setFieldValue(["consultants", prefix], hod.user_id);
    onPersonSelect(prefix, {
      name:
        hod.full_name ||
        `${hod.first_name || ""} ${hod.last_name || ""}`.trim(),
      designation: hod.designation,
    });
  };

  useEffect(() => {
    const fetchPractitioners = async (role, setState, prefix) => {
      try {
        const response = await getPractitionersListApi(
          `facility=${facilityId}&role=${role}`
        );
        const list = response?.data?.data || [];
        setState(list);
        prefillHeadOfDepartment(list, prefix);
      } catch (error) {
        console.error(`Error fetching ${role} list:`, error);
      }
    };

    fetchPractitioners("Doctor", setConsultants, "consultant");
    fetchPractitioners("Physiotherapist", setPhysiotherapists, "physiotherapist");
    fetchPractitioners("Nutritionist", setNutritionists, "nutritionist");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const personOptions = (list) =>
    list.map((c) => ({
      value: c.user_id,
      label: (
        <Typography.Text>
          {c.first_name + " " + c.last_name}
          {c.designation && <Tag>{c.designation}</Tag>}
        </Typography.Text>
      ),
      name: c.first_name + " " + c.last_name,
      designation: c.designation,
    }));

  const onPersonSelect = (prefix, option) => {
    form.setFieldValue([`consultants`, `${prefix}_name`], option?.name);
    form.setFieldValue(
      [`consultants`, `${prefix}_designation`],
      option?.designation
    );
    form.setFieldValue(
      [`consultants`, `${prefix}_description`],
      option?.designation
    );
  };

  return (
    <>
      <Form.Item name={["consultants", "consultant_name"]} hidden />
      <Form.Item name={["consultants", "consultant_designation"]} hidden />
      <Form.Item name={["consultants", "physiotherapist_name"]} hidden />
      <Form.Item name={["consultants", "physiotherapist_designation"]} hidden />
      <Form.Item name={["consultants", "nutritionist_name"]} hidden />
      <Form.Item name={["consultants", "nutritionist_designation"]} hidden />

      <Row gutter={[16, 0]} className="forms-ip-consultant-row">
        <Col {...FIELD_COL}>
          <Form.Item
            label="Consultant Name"
            name={["consultants", "consultant"]}
          >
            <Select
              className={inputStypes.mdSelect}
              style={{ width: "100%" }}
              options={personOptions(consultants)}
              onSelect={(_, option) => onPersonSelect("consultant", option)}
            />
          </Form.Item>
        </Col>
        <Col {...FIELD_COL}>
          <Form.Item
            label="Qualification & Designation"
            name={["consultants", "consultant_description"]}
          >
            <Input className={inputStypes.mdInput} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]} className="forms-ip-consultant-row">
        <Col {...FIELD_COL}>
          <Form.Item
            label="Physiotherapist"
            name={["consultants", "physiotherapist"]}
          >
            <Select
              className={inputStypes.mdSelect}
              style={{ width: "100%" }}
              options={personOptions(physiotherapists)}
              onSelect={(_, option) => onPersonSelect("physiotherapist", option)}
            />
          </Form.Item>
        </Col>
        <Col {...FIELD_COL}>
          <Form.Item
            label="Qualification & Designation"
            name={["consultants", "physiotherapist_description"]}
          >
            <Input className={inputStypes.mdInput} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]} className="forms-ip-consultant-row">
        <Col {...FIELD_COL}>
          <Form.Item
            label="Nutritionist"
            name={["consultants", "nutritionist"]}
          >
            <Select
              className={inputStypes.mdSelect}
              style={{ width: "100%" }}
              options={personOptions(nutritionists)}
              onSelect={(_, option) => onPersonSelect("nutritionist", option)}
            />
          </Form.Item>
        </Col>
        <Col {...FIELD_COL}>
          <Form.Item
            label="Qualification & Designation"
            name={["consultants", "nutritionist_description"]}
          >
            <Input className={inputStypes.mdInput} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]} className="forms-ip-consultant-row">
        <Col {...FIELD_COL}>
          <Form.Item
            label="Specialization"
            name={["consultants", "specialization"]}
          >
            <Input className={inputStypes.mdInput} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col {...FIELD_COL}>
          <Form.Item
            label="Specialist Consultant"
            name={["consultants", "specialist_consultant"]}
          >
            <Input className={inputStypes.mdInput} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col {...FIELD_COL}>
          <Form.Item
            label="Qualification & Designation"
            name={["consultants", "qualification"]}
          >
            <Input className={inputStypes.mdInput} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default ConsultantForm;
